import { z } from 'zod';

import { stockSourceConfigSchema } from './stock-source-config.schema';

// A `stock_sources` table row: a named site-inventory sync config. CRUD via
// /stock-sources; searched by agents via POST /internal/tools/stock-search.

const nameSchema = z
  .string()
  .min(2, 'Stock source name must be at least 2 characters')
  .max(100, 'Stock source name must be at most 100 characters')
  .regex(
    /^[a-z_][a-z0-9_]*$/,
    'Stock source name must be snake_case (lowercase letters, numbers, and underscores only)',
  );

export const createStockSourceSchema = z.object({
  name: nameSchema,
  description: z.string().optional(),
  config: stockSourceConfigSchema,
  // Raw Cookie header for session-gated sites; encrypted at rest, never returned.
  cookie_header: z.string().optional(),
  sync_interval_minutes: z.number().int().min(15).max(43200).optional().default(1440),
  is_active: z.boolean().optional().default(true),
});
export type CreateStockSourceDto = z.infer<typeof createStockSourceSchema>;

export const updateStockSourceSchema = z
  .object({
    name: nameSchema,
    description: z.string(),
    config: stockSourceConfigSchema,
    cookie_header: z.string(),
    sync_interval_minutes: z.number().int().min(15).max(43200),
    is_active: z.boolean(),
  })
  .partial();
export type UpdateStockSourceDto = z.infer<typeof updateStockSourceSchema>;

export const stockSourceQuerySchema = z.object({
  search: z.string().optional(),
  is_active: z.coerce.boolean().optional(),
});
export type StockSourceQuery = z.infer<typeof stockSourceQuerySchema>;

// Ad-hoc test: crawl a few pages with an unsaved config, no DB writes.
export const testStockSourceSchema = z.object({
  config: stockSourceConfigSchema,
  cookie_header: z.string().optional(),
  max_pages: z.number().int().min(1).max(3).optional().default(2),
});
export type TestStockSourceDto = z.infer<typeof testStockSourceSchema>;

// LLM-facing search request (POST /internal/tools/stock-search). source_id is
// pinned per agent as a fixed_value param on its custom tool.
export const stockSearchSortSchema = z.enum([
  'price_asc',
  'price_desc',
  'year_desc',
  'mileage_asc',
  'newest',
]);
export type StockSearchSort = z.infer<typeof stockSearchSortSchema>;

export const stockSearchRequestSchema = z.object({
  source_id: z.string().uuid(),
  make: z.string().optional(),
  model: z.string().optional(),
  price_min: z.coerce.number().int().positive().optional(),
  price_max: z.coerce.number().int().positive().optional(),
  year_min: z.coerce.number().int().optional(),
  year_max: z.coerce.number().int().optional(),
  mileage_max: z.coerce.number().int().positive().optional(),
  fuel_type: z.string().optional(),
  transmission: z.string().optional(),
  body_type: z.string().optional(),
  colour: z.string().optional(),
  keyword: z.string().optional(),
  page: z.coerce.number().int().min(1).optional().default(1),
  page_size: z.coerce.number().int().min(1).max(25).optional().default(10),
  sort: stockSearchSortSchema.optional().default('price_asc'),
});
export type StockSearchRequestDto = z.infer<typeof stockSearchRequestSchema>;

export interface StockSyncRunResult {
  run_id: string;
  status: 'success' | 'failed' | 'auth_expired';
  pages_fetched: number;
  records_parsed: number;
  upserted: number;
  marked_unavailable: number;
  error?: string;
}

export interface StockEngineResult {
  completed: boolean;
  auth_expired: boolean;
  pages_fetched: number;
  records_parsed: number;
  error?: string;
  sample_records: Record<string, unknown>[];
}
